//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  Like2BuyV2ModerationItemFilter,
  Like2BuyV2ModerationStatus,
} from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import { TEMPLATE_GRID_CARD_WIDTH } from '@components/templates/templateConstants';
import { DEFAULT_INSTAGRAM_CUSTOMIZATIONS } from '@instagram-account/like2buy-v2/constants/like2BuyV2InstagramConstants';
import { Like2BuyV2Context } from '@like2buy-v2/context/Like2BuyV2Context';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import NetworkUserId from '@models/NetworkUserId';
import { SocialNetwork } from '@models/SocialNetwork';

import { DashboardStateNames } from 'app/top-level-states';

export const LIKE2BUY_V2_ITEM_REQUEST_LIMIT = 50;
// pinned items should not exceed the fetch request limit
export const MAXIMUM_PINNED_ITEMS_COUNT = LIKE2BUY_V2_ITEM_REQUEST_LIMIT;

// distribution channel that has rights permissions
export const REQUIRED_LIKE2BUY_DISTRIBUTION_CHANNEL = 'Social';

export const CONTACT_SUPPORT_MESSAGE =
  'Please try again or contact Bazaarvoice Support.';

export const CONTENT_CARD_WIDTH = TEMPLATE_GRID_CARD_WIDTH;

export const SQUARE_MEDIA_LENGTH = CONTENT_CARD_WIDTH;

export const CONTENT_CARD_DETAILS_HEIGHT = 108;

export const INSTAGRAM_ASPECT_RATIO = 1 / 1;
export const TIKTOK_ASPECT_RATIO = 3 / 4;

export const getAspectRatioByNetwork = (network: SocialNetwork) => {
  switch (network) {
    case SocialNetwork.Instagram:
      return INSTAGRAM_ASPECT_RATIO;
    case SocialNetwork.TikTok:
      return TIKTOK_ASPECT_RATIO;
    case SocialNetwork.Facebook: {
      throw new Error('Like2Buy for SocialNetwork.Facebook not supported');
    }
    case SocialNetwork.Pinterest: {
      throw new Error('Like2Buy for SocialNetwork.Pinterest not supported');
    }
    case SocialNetwork.Snapchat: {
      throw new Error('Like2Buy for SocialNetwork.Snapchat not supported');
    }
    case SocialNetwork.Tumblr: {
      throw new Error('Like2Buy for SocialNetwork.Tumblr not supported');
    }
    case SocialNetwork.Twitter: {
      throw new Error('Like2Buy for SocialNetwork.Twitter not supported');
    }
    case SocialNetwork.YouTube: {
      throw new Error('Like2Buy for SocialNetwork.YouTube not supported');
    }
    case SocialNetwork.Unknown: {
      throw new Error('Like2Buy for SocialNetwork.Unknown not supported');
    }
    case SocialNetwork.Bazaarvoice: {
      throw new Error('Like2Buy for SocialNetwork.Bazaarvoice not supported');
    }
  }
};

export const INSTAGRAM_LIKE2BUYV2_PREVIEW_URL: DashboardStateNames =
  'instagramAccount.like2buy-preview';

export const TIKTOK_LIKE2BUYV2_PREVIEW_URL: DashboardStateNames =
  'tiktok.like2buy.preview';

export const getPreviewUrlByNetwork = (network: SocialNetwork) => {
  switch (network) {
    case SocialNetwork.Instagram:
      return INSTAGRAM_LIKE2BUYV2_PREVIEW_URL;
    case SocialNetwork.TikTok:
      return TIKTOK_LIKE2BUYV2_PREVIEW_URL;
    case SocialNetwork.Facebook: {
      throw new Error('Like2Buy for SocialNetwork.Facebook not supported');
    }
    case SocialNetwork.Pinterest: {
      throw new Error('Like2Buy for SocialNetwork.Pinterest not supported');
    }
    case SocialNetwork.Snapchat: {
      throw new Error('Like2Buy for SocialNetwork.Snapchat not supported');
    }
    case SocialNetwork.Tumblr: {
      throw new Error('Like2Buy for SocialNetwork.Tumblr not supported');
    }
    case SocialNetwork.Twitter: {
      throw new Error('Like2Buy for SocialNetwork.Twitter not supported');
    }
    case SocialNetwork.YouTube: {
      throw new Error('Like2Buy for SocialNetwork.YouTube not supported');
    }
    case SocialNetwork.Unknown: {
      throw new Error('Like2Buy for SocialNetwork.Unknown not supported');
    }
    case SocialNetwork.Bazaarvoice: {
      throw new Error('Like2Buy for SocialNetwork.Bazaarvoice not supported');
    }
  }
};

export const calculateMediaHeight = (aspectRatio: number, width: number) =>
  width / aspectRatio;

export const calculateContentCardHeight = (
  aspectRatio: number,
  width: number,
) => calculateMediaHeight(aspectRatio, width) + CONTENT_CARD_DETAILS_HEIGHT;

export const PINNED_MEDIA_WIDTH = 86;

export const MAX_PREVIEW_LENGTH = 260;

export const Like2BuyV2BadgeText: Readonly<
  Record<
    | Exclude<keyof typeof Like2BuyV2ModerationStatus, 'Pinned'>
    | 'NeedsProductsAndRightsTruncated',
    string
  >
> = {
  Archived: 'Archived',
  Published: 'Published',
  NeedsProducts: 'Needs Products',
  NeedsRights: 'Needs Rights',
  NeedsProductsAndRights: 'Needs Products and Rights',
  NeedsProductsAndRightsTruncated: 'Needs Products ...',
} as const;

export const DEFAULT_LIKE2BUY_V2_CONTEXT: Like2BuyV2Context = {
  currentItemFilter: Like2BuyV2ModerationItemFilter.AllMedia,
  customizations: DEFAULT_INSTAGRAM_CUSTOMIZATIONS,
  isPreviewModalOpen: false,
  network: SocialNetwork.Instagram,
  networkUserId: '' as NetworkUserId,
  storeId: '' as Like2BuyV2StoreId,
  setCurrentItemFilter: () => undefined,
  setCustomizations: () => undefined,
  setIsPreviewModalOpen: () => undefined,
  setLike2BuyV1Banners: () => undefined, // TODO PD-245819: Clean up dual write
};

export const Like2BuyV2Filters = (network: SocialNetwork) => [
  {
    value: Like2BuyV2ModerationItemFilter.AllMedia,
    label: 'All Media',
    contextText: `We will automatically publish media with tagged products and rights approved to your ${network} Like2Buy store.`,
  },
  {
    value: Like2BuyV2ModerationItemFilter.ActionNeeded,
    label: 'Action Needed',
    contextText: `Tag products or request rights for this media to publish to your ${network} Like2Buy store.`,
  },
  {
    value: Like2BuyV2ModerationItemFilter.Published,
    label: 'Published',
    contextText: `Published media currently appears on your ${network} Like2Buy store.`,
  },
  {
    value: Like2BuyV2ModerationItemFilter.Archived,
    label: 'Archived',
    contextText: `Archived media does not appear in your ${network} Like2Buy store.`,
  },
];
